import {
  Dispatch,
  SetStateAction,
  KeyboardEvent,
  ReactElement,
  ReactNode,
  createContext,
  forwardRef,
  useContext,
  useId,
  useMemo,
  useRef,
  useState,
  Children,
  isValidElement,
  type CSSProperties,
} from 'react';

import { delve } from '@eneco/common/delve';

import {
  cellRecipe,
  sparkyTableCellClassName,
  headerStyle,
  tableBodyStyle,
  sectionHeadingRecipe,
  indicatorStyle,
  stickyTableContentStyle,
  relativeCellStyle,
  rowStyle,
  rowFooterStyle,
  wrapperStyle,
  tableStyle,
  captionStyle,
} from './Table.css';
import { useIsomorphicLayoutEffect } from '../../hooks/useIsomorphicLayoutEffect';
import { ChevronUpIcon } from '../../icons';
import { getClassNames } from '../../util/css';

const sparkyTableClassName = 'sparky-table';

// ─── Cell (internal helper) ────────────────────────────────────────────────────

interface CellProps {
  as?: 'td' | 'th';
  scope?: string;
  colSpan?: number;
  hasRightBorder?: boolean;
  isStickyLeft?: boolean;
  isStickyTop?: boolean;
  isTwoColumns?: boolean;
  style?: CSSProperties;
  className?: string;
  children?: ReactNode;
}

const Cell = forwardRef<HTMLTableCellElement, CellProps>(
  (
    {
      as: Tag = 'td',
      scope,
      colSpan,
      hasRightBorder,
      isStickyLeft,
      isStickyTop,
      isTwoColumns,
      style,
      className,
      children,
    },
    ref,
  ) => {
    const classNames = getClassNames(
      sparkyTableCellClassName,
      cellRecipe({ hasRightBorder, isStickyLeft, isStickyTop, isTwoColumns }),
      className,
    );
    return (
      <Tag ref={ref} className={classNames} scope={scope} colSpan={colSpan} style={style}>
        {children}
      </Tag>
    );
  },
);

// ─── Header ────────────────────────────────────────────────────────────────────

type HeaderProps = { children: ReactElement[] };

const Header = forwardRef<HTMLTableSectionElement, HeaderProps>(({ children }, ref) => {
  const { hasLeftLegend } = useTableContext();

  return (
    <thead ref={ref} className={headerStyle}>
      <tr className={rowStyle}>
        {hasLeftLegend ? <Cell isStickyLeft isStickyTop style={{ zIndex: 2 }} /> : null}
        {Children.map(children, (child, i) => (
          <Cell isStickyTop as="th" scope="col" style={{ zIndex: 1 }} isTwoColumns={children.length === 2 && i === 1}>
            {child}
          </Cell>
        ))}
      </tr>
    </thead>
  );
});

// ─── Section ───────────────────────────────────────────────────────────────────

type SectionProps = {
  title: string;
  children: ReactNode;
} & StateProps;

type StateProps =
  | {
      isCollapsible?: boolean;
      isOpen?: never;
      setOpen?: never;
    }
  | {
      isCollapsible: true;
      /** Controlled state of the modal */
      isOpen: boolean;
      /** State setter for the controlled state. Required in order to use built-in controls. */
      setOpen: Dispatch<SetStateAction<boolean>>;
    };

const Section = forwardRef<HTMLTableSectionElement, SectionProps>(
  ({ children, title, isCollapsible, isOpen: isOpenPassed, setOpen: setOpenPassed }, ref) => {
    const [isOpen, setOpen] = useState(true);
    const { width } = useTableContext();

    const isExpanded = isOpenPassed !== undefined ? isOpenPassed : isOpen;

    const clickHandler = () => {
      if (isOpenPassed !== undefined) {
        setOpenPassed(!isOpenPassed);
      } else {
        setOpen(!isOpen);
      }
    };

    const sectionId = useId();

    const keyHandler = (e: KeyboardEvent<HTMLTableRowElement>) => {
      if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
        if (isOpenPassed !== undefined) {
          setOpenPassed(!isOpenPassed);
        } else {
          setOpen(!isOpen);
        }
      }
    };

    const relativeCellClassNames = getClassNames(sparkyTableCellClassName, cellRecipe({}), relativeCellStyle);

    if (!isCollapsible) {
      return (
        <tbody ref={ref} className={tableBodyStyle}>
          <tr className={sectionHeadingRecipe({})}>
            <th className={relativeCellClassNames} scope="rowgroup" colSpan={100}>
              <div className={stickyTableContentStyle} style={{ width: `${width}px` }}>
                {title}{' '}
              </div>
            </th>
          </tr>
          {children}
        </tbody>
      );
    }

    return (
      <tbody id={sectionId} data-state={isExpanded ? 'open' : 'closed'} ref={ref} className={tableBodyStyle}>
        <tr
          className={sectionHeadingRecipe({ clickable: true })}
          onKeyDown={keyHandler}
          onClick={clickHandler}
          role="button"
          tabIndex={0}
          aria-controls={sectionId}
          aria-expanded={isExpanded}>
          <th className={relativeCellClassNames} scope="rowgroup" colSpan={100}>
            <div className={stickyTableContentStyle} style={{ width: `${width}px` }}>
              {title}
              <span className={indicatorStyle}>
                <ChevronUpIcon color="iconPrimary" />
              </span>
            </div>
          </th>
        </tr>
        {children}
      </tbody>
    );
  },
);

// ─── Row ───────────────────────────────────────────────────────────────────────

type RowProps = {
  isFooter?: boolean;
  children: ReactElement[];
};

const Row = forwardRef<HTMLTableRowElement, RowProps>(({ children, isFooter }, ref) => {
  const { hasLeftLegend } = useTableContext();
  const rowClassNames = getClassNames(rowStyle, isFooter && rowFooterStyle);
  return (
    <tr className={rowClassNames} ref={ref}>
      {Children.map(children, (child, i) => {
        if (hasLeftLegend && i === 0) {
          return (
            <Cell as="th" scope="row" hasRightBorder isStickyLeft>
              {child}
            </Cell>
          );
        }
        return <Cell isTwoColumns={children.length === 2 && i === 1}>{child}</Cell>;
      })}
    </tr>
  );
});

// ─── Context ───────────────────────────────────────────────────────────────────

type TableContextType = {
  hasLeftLegend: boolean;
  width: number;
};

const TableContext = createContext<TableContextType>({
  hasLeftLegend: false,
  width: 0,
});

const useTableContext = (): TableContextType => useContext(TableContext);

// ─── Table ─────────────────────────────────────────────────────────────────────

type TableProps = {
  children: ReactNode;
  /** Makes the first element in each row a header and also sticky. */
  hasLeftLegend?: boolean;
  /** Title that will become the caption of the table. Do not use in conjunction with a header. */
  title?: string;
  className?: never;
};

const TableComponent = forwardRef<HTMLTableElement, TableProps>(
  ({ children, className = '', hasLeftLegend, title }, ref) => {
    const [width, setWidth] = useState(0);
    const rowChildren: ReactElement[] = [];
    const nonRowChildren = Children.map(children, child => {
      if (isValidElement(child) && delve(child, 'type.displayName') === 'Table.Row') {
        rowChildren.push(child);
      } else {
        return child;
      }
    });

    const wrapperRef = useRef<HTMLTableElement>(null);
    useIsomorphicLayoutEffect(() => {
      const updateWidth = () => {
        if (wrapperRef?.current) {
          setWidth(wrapperRef.current.clientWidth);
        }
      };
      updateWidth();

      window.addEventListener('resize', updateWidth);

      return () => {
        window.removeEventListener('resize', updateWidth);
      };
    }, []);

    const tableClassNames = getClassNames(sparkyTableClassName, tableStyle, className);

    const tableContextValue = useMemo(() => ({ hasLeftLegend: !!hasLeftLegend, width }), [hasLeftLegend, width]);

    return (
      <TableContext.Provider value={tableContextValue}>
        <div ref={wrapperRef} className={wrapperStyle}>
          <table ref={ref} className={tableClassNames}>
            {title && <caption className={captionStyle}>{title}</caption>}
            {nonRowChildren}
            {rowChildren.length ? <tbody className={tableBodyStyle}>{rowChildren}</tbody> : null}
          </table>
        </div>
      </TableContext.Provider>
    );
  },
);

// Compound components with forwardRef cause issues:
// https://github.com/DefinitelyTyped/DefinitelyTyped/issues/34757#issuecomment-488848720
export const Table = Object.assign({}, TableComponent, { Header, Section, Row });

Table.toString = () => `.${sparkyTableClassName}`;

TableComponent.displayName = 'Table';
Table.Header.displayName = 'Table.Header';
Table.Section.displayName = 'Table.Section';
Table.Row.displayName = 'Table.Row';
